/**
 * Sprint 5-a · AgenticChatPanel · Genspark 式 plan-execute-review loop + cost transparency。
 *
 * 流程：告知計畫(PlanPreview) → 確認製作 → 逐步製作(StepProgress·SSE 串流) → 詢問修改
 * (StepReviewDialog) → 完成後詢問繼續(PlanCompletionDialog)。CostMeter sticky 永遠可見。
 *
 * 「應答就計費」：每次 AI 應答（plan / execute / replan）都計費並即時顯示；中斷/取消明確告知
 * 「已用 tokens 仍計」。串流為 server-side 分塊模擬（adapter sync·非原生 token 串流）。
 *
 * voice-ready pattern：本面板即 Sprint 6 語音管家的 UI 骨架（語音只加 STT 輸入層）。
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../../../../contexts/MessageContext';
import {
  abortPlan, completePlan, confirmPlan, createPlan, streamStep,
  OfficeAction, PlanResponse, StepDone,
} from '../services/aiOfficeService';
import { actionToInsert } from './AiOfficePanel';
import { STRUCTURAL_OFFICE_TOOLS } from '../services/officeActionMessages';
import CostMeter from './CostMeter';
import PlanPreview from './PlanPreview';
import StepProgress from './StepProgress';
// hotfix-1：review / completion 由 modal Dialog 改為 chat panel 內 inline banner（不擋左側 Excel）
import StepReviewBanner from './StepReviewBanner';
import PlanCompletionBanner from './PlanCompletionBanner';

type Phase = 'idle' | 'planning' | 'plan_ready' | 'executing' | 'review' | 'completed' | 'aborted' | 'step_error';

interface Props {
  fileObjectId?: number;
  mimeType: string;
  filename: string;
  onInsert: (content: string, style?: string) => void;
  onRefineReplace: (content: string) => void;
  onOfficeAction?: (name: string, input: Record<string, unknown>) => void;   // hotfix-2 · xlsx 結構化工具
  onClose?: () => void;
}

const AgenticChatPanel: React.FC<Props> = ({ fileObjectId, mimeType, filename, onInsert, onRefineReplace, onOfficeAction, onClose }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`agentic.${k}`, d);
  const { showApiError, showError, showSuccess } = useMessage();

  const [phase, setPhase] = useState<Phase>('idle');
  const [input, setInput] = useState('');
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepTitle, setStepTitle] = useState('');
  const [streamText, setStreamText] = useState('');
  const [lastDone, setLastDone] = useState<StepDone | null>(null);
  const [sessionTokens, setSessionTokens] = useState(0);
  const [sessionCost, setSessionCost] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [abortInfo, setAbortInfo] = useState<{ tokens: number; cost: number } | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const contextRef = useRef<string>('');   // 跨計畫上下文（提新需求延續）
  const runningRef = useRef(false);
  const streamTextRef = useRef('');        // streamText 最新值（onDone closure 內取用）
  streamTextRef.current = streamText;
  const scrollRef = useRef<HTMLDivElement>(null);   // hotfix-1：banner render 完自動捲到底
  // session 用量 = 先前已完成計畫累計(prior) + 目前計畫的 cumulative（後端回·已含 plan 生成）
  const priorTokensRef = useRef(0);
  const priorCostRef = useRef(0);
  const curTokensRef = useRef(0);
  const curCostRef = useRef(0);

  const syncSession = () => {
    setSessionTokens(priorTokensRef.current + curTokensRef.current);
    setSessionCost(priorCostRef.current + curCostRef.current);
  };
  const foldCurrentIntoPrior = () => {
    priorTokensRef.current += curTokensRef.current;
    priorCostRef.current += curCostRef.current;
    curTokensRef.current = 0;
    curCostRef.current = 0;
  };

  // unmount：中止進行中串流（route unmount cleanup·避免懸掛連線）
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // hotfix-1：串流 / 進入 review / 完成時捲到底，確保 inline banner 立即可見（不隱藏在下方）
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [phase, streamText, lastDone]);

  const totalSteps = plan?.steps.length || 0;

  const applyActions = (a: OfficeAction) => {
    try {
      // hotfix-2 · xlsx 結構化工具（公式 / 合併 / 凍結 / 格式 / 新 sheet）→ 走 postMessage 序列派送
      if (onOfficeAction && STRUCTURAL_OFFICE_TOOLS.has(a.name)) {
        onOfficeAction(a.name, a.input || {});
        return;
      }
      // 內容產生器（insert_text / build_xlsx_table / …）→ 轉字串貼入
      const r = actionToInsert(a);
      if (!r.content) return;
      if (r.kind === 'replace') onRefineReplace(r.content);
      else onInsert(r.content, r.style);
    } catch { /* silent ok: 套用單一動作失敗不中斷整體流程（使用者可手動再試） */ }
  };

  // ── plan ───────────────────────────────────────────────────────────────────
  const doPlan = async (prompt: string, parentContext?: string) => {
    setPhase('planning');
    try {
      const resp = await createPlan({
        original_prompt: prompt, mime_type: mimeType, filename,
        file_object_id: fileObjectId, parent_context: parentContext,
      });
      setPlan(resp);
      curTokensRef.current = resp.session.tokens;
      curCostRef.current = resp.session.cost;
      syncSession();
      setCurrency(resp.currency || 'USD');
      setPhase('plan_ready');
    } catch (err) {
      showApiError(err);
      setPhase('idle');
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text || phase === 'planning') return;
    setInput('');
    setAbortInfo(null);
    doPlan(text, contextRef.current || undefined);
  };

  // ── execute step（含 replan）────────────────────────────────────────────────
  const runStep = async (idx: number, reason: 'execute' | 'replan', feedback?: string) => {
    if (!plan) return;
    if (reason === 'execute' && idx === 0 && plan.status === 'pending') {
      try { await confirmPlan(plan.plan_id); } catch (err) { showApiError(err); return; }
    }
    setStepIndex(idx);
    setStreamText('');
    setLastDone(null);
    setPhase('executing');
    runningRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;

    await streamStep(plan.plan_id, idx, { reason, feedback }, {
      onStart: (d) => setStepTitle(d.title),
      onDelta: (chunk) => setStreamText((v) => v + chunk),
      onAction: (a) => applyActions(a),
      onDone: (d) => {
        runningRef.current = false;
        setLastDone(d);
        curTokensRef.current = d.cumulative_tokens;   // 後端 cumulative 已含 plan 生成 + 各 step
        curCostRef.current = d.cumulative_cost;
        syncSession();
        setCurrency(d.currency || 'USD');
        contextRef.current = (contextRef.current + '\n' + streamTextRef.current).slice(-3000);
        setPhase('review');
      },
      onError: () => {
        runningRef.current = false;
        showError('ERR390024');   // 步驟執行失敗
        setPhase('step_error');
      },
    }, controller.signal);
  };

  // ── review 決策 ─────────────────────────────────────────────────────────────
  const handleContinue = async () => {
    if (!lastDone) return;
    if (lastDone.is_last) {
      await doComplete();
    } else {
      runStep(stepIndex + 1, 'execute');
    }
  };

  const handleReplan = (feedback: string) => runStep(stepIndex, 'replan', feedback);

  const doAbort = async () => {
    abortRef.current?.abort();
    runningRef.current = false;
    if (!plan) return;
    try {
      const r = await abortPlan(plan.plan_id, 'aborted', stepIndex);
      setAbortInfo({ tokens: r.aborted_tokens, cost: r.aborted_cost });
    } catch { /* silent ok: 中斷回報失敗僅稽核 trail 稍缺，UI 仍進中斷態 */ }
    setPhase('aborted');
  };

  const doComplete = async () => {
    if (!plan) return;
    try {
      const r = await completePlan(plan.plan_id);
      showSuccess('SYS390020', { tokens: String(r.total_tokens) });
    } catch (err) { showApiError(err); }
    setPhase('completed');
  };

  const cancelPlan = async () => {
    if (plan) {
      try {
        await abortPlan(plan.plan_id, 'cancelled');
      } catch {
        // silent ok: 取消回報失敗僅稽核 trail 稍缺，UI 仍需回到 idle
      }
    }
    resetToIdle(false);
  };

  const resetToIdle = (clearSession: boolean) => {
    setPlan(null);
    setStreamText('');
    setLastDone(null);
    setAbortInfo(null);
    setPhase('idle');
    if (clearSession) {
      // 結束對話：整個 session 歸零
      priorTokensRef.current = 0; priorCostRef.current = 0;
      curTokensRef.current = 0; curCostRef.current = 0;
      contextRef.current = '';
      setSessionTokens(0);
      setSessionCost(0);
    } else {
      // 提新需求：把剛結束的計畫用量收進 prior，累計沿用
      foldCurrentIntoPrior();
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1"><strong>🤖 {tt('title', 'AI Office 助手')}</strong></Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">{filename}</Typography>
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label={t('common.close', '關閉')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {phase === 'idle' && !plan && (
          <Typography variant="body2" color="text.secondary">
            {tt('hint', '描述你要的成果，AI 會先規劃步驟再逐步製作。例如：「建立一份收入支出表」')}
          </Typography>
        )}

        {phase === 'planning' && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">🧠 {tt('planning', '規劃步驟中…')}</Typography>
          </Stack>
        )}

        {phase === 'plan_ready' && plan && (
          <PlanPreview steps={plan.steps} estimatedTokens={plan.estimated_total_tokens}
            onConfirm={() => runStep(0, 'execute')} onCancel={cancelPlan} />
        )}

        {(phase === 'executing' || phase === 'review' || phase === 'step_error') && plan && (
          <StepProgress stepNo={stepIndex + 1} totalSteps={totalSteps} title={stepTitle}
            streamingText={streamText} running={phase === 'executing'}
            doneTokens={lastDone?.tokens} doneCost={lastDone?.cost}
            cumulativeTokens={lastDone?.cumulative_tokens} onAbort={doAbort} />
        )}

        {/* hotfix-1 · 每步審核 · inline banner（不擋左側 Excel·使用者可先檢視 sheet 再決定）*/}
        {phase === 'review' && (
          <StepReviewBanner stepNo={stepIndex + 1} isLast={!!lastDone?.is_last}
            onContinue={handleContinue} onReplan={handleReplan} onAbort={doAbort} />
        )}

        {phase === 'step_error' && (
          <Alert severity="error" sx={{ mb: 1 }}
            action={<Button size="small" onClick={() => runStep(stepIndex, 'execute')}>{tt('retry', '重試')}</Button>}>
            {tt('stepError', '這一步執行失敗了')}
          </Alert>
        )}

        {phase === 'aborted' && abortInfo && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {tt('abortedBanner', '已中斷')} · {tt('used', '這次用了')} {abortInfo.tokens.toLocaleString()} tokens ·
            ${abortInfo.cost.toFixed(4)}
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {tt('abortedNote', '中斷但已消耗的 token 仍會計入本月用量')}
            </Typography>
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => resetToIdle(false)}>{tt('newRequest', '提新需求')}</Button>
          </Alert>
        )}

        {/* hotfix-1 · 完成 · inline banner（non-blocking·提新需求 / 結束對話）*/}
        {phase === 'completed' && (
          <PlanCompletionBanner totalTokens={curTokensRef.current} totalCost={curCostRef.current}
            onNewRequest={() => resetToIdle(false)} onFinish={() => resetToIdle(true)} />
        )}
      </Box>

      {/* 輸入列：idle / completed / aborted 可提新需求 */}
      {(phase === 'idle' || phase === 'aborted' || phase === 'completed') && (
        <Box sx={{ p: 1, borderTop: '1px solid #eee' }}>
          <Stack direction="row" spacing={1}>
            <TextField size="small" fullWidth multiline maxRows={4} value={input}
              placeholder={tt('placeholder', '描述你要的成果…')}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <Button variant="contained" disabled={!input.trim()} onClick={send}>{tt('send', '送出')}</Button>
          </Stack>
        </Box>
      )}

      {/* CostMeter · sticky · always visible */}
      <CostMeter sessionTokens={sessionTokens} sessionCost={sessionCost} currency={currency} />
    </Box>
  );
};

export default AgenticChatPanel;
